import { UniButton } from '@/design-system/components/buttons';
import { TextCaption, TextSmall, TextTitle } from '@/design-system/components/typography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useNavigation } from '@react-navigation/native';
import { styled, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, useWindowDimensions } from 'react-native';
import { RootStackRoutes } from 'types';
import { GetBrokersResponseItem } from '@utx/utx-client';
import SVGBinance from '../../../../assets/svg/BinanceBrokerBanner.svg';
import SVGBannerBitfinex from '../../../../assets/svg/BitfinexBrokerBanner.svg';
import { ExchangeSubAccountType } from '../../enum';
import { ExchangeSubAccountStackRoutes } from '../../exchange.navigator';
import { UseGetCalcFeeProp } from '../../hooks/useGetCalcFee.hook';
import { ScaledImage } from '../components/ScaledImage';

export interface SelectExchangeProps {
  data: GetBrokersResponseItem;
  calcFee: UseGetCalcFeeProp;
  onResult: () => void;
}

export const SelectExchangeItemNative: React.FC<SelectExchangeProps> = (props) => {
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const { data, calcFee, onResult } = props;
  const {
    monthlyData: { fee: monthlyFee },
    applicationData: { fee: applicationFee }
  } = calcFee;
  const { userFee: applicationUserFee, feeCurrency: applicationFeeCurrency } = applicationFee || {};
  const { userFee: monthlyUserFee, feeCurrency: monthlyFeeCurrency } = monthlyFee || {};
  const metadata = data.metadata || {};
  const { feeSummary } = metadata || {};
  const { estimatedAccountOpeningTime } = feeSummary || {};
  const ImageView = styled(Image)();
  const getIcon = (name: string) => {
    if (name === ExchangeSubAccountType.BITFINEX) {
      return <SVGBannerBitfinex height="100%" preserveAspectRatio="xMinYMin slice" width="100%" viewBox="0 0 375 1114" />;
    }
    if (name === ExchangeSubAccountType.BINANCE) {
      return <SVGBinance height="100%" preserveAspectRatio="xMinYMin slice" width="100%" viewBox="0 0 375 1114" />;
    }
    return <ImageView sx={{ width: '100%', height: '100%' }} source={require('../../../../assets/images/ftxBrokerBanner.jpg')} />;
  };

  const getBackgroundColor = (name: string) => {
    if (name === ExchangeSubAccountType.BITFINEX) {
      return '#275D6C';
    }
    if (name === ExchangeSubAccountType.BINANCE) {
      return '#FDFADC';
    }
    return '#3276A7';
  };

  const displayAmountWithCurrency = (amountString: string, currency: string) => {
    const amount = parseFloat(amountString ?? '0');
    return `${amount < 0 ? '-' : ''}${formatAmountByDecimals({ value: Math.abs(amount), decimals: 2 })} ${currency ?? 'HKD'}`;
  };

  const { itemLayout, itemBanner, itemView, itemViewSmall, itemContainer, itemLeft, itemRight } = sxStyles;
  const withCard = width - 32;
  const heightImage = withCard * 0.484848484848485;
  const heightContent = withCard * 0.495626822157434;
  const styleLayoutContent = width >= 375 ? { ...itemView, ...{ height: heightContent } } : itemView;
  const { t } = useTranslation('exchange-sub-account');

  return (
    <View sx={{ width: withCard }}>
      <View sx={itemLayout}>
        <View sx={{ ...itemBanner, ...{ backgroundColor: getBackgroundColor(data.name), height: heightImage } }}>
          <View sx={sxStyles.iconWrapper}>
            <ScaledImage uri={data.logoUrl ?? ''} />
          </View>
          {getIcon(data.name)}
        </View>
        <View sx={styleLayoutContent}>
          <View sx={itemContainer}>
            <View sx={itemLeft}>
              <TextCaption sx={sxStyles.feeText}>{t('exchange-account::glossary.account-application-fee')}</TextCaption>
              <TextTitle bold>{displayAmountWithCurrency(applicationUserFee, applicationFeeCurrency)}</TextTitle>
            </View>
            <View sx={itemRight}>
              <TextCaption sx={sxStyles.monthlyFeeText}>{t('exchange-account::glossary.monthly-fee')}</TextCaption>
              <TextTitle bold>
                {displayAmountWithCurrency(monthlyUserFee, !_.isEmpty(monthlyFeeCurrency) ? monthlyFeeCurrency : applicationFeeCurrency)}
              </TextTitle>
            </View>
          </View>
          <View sx={sxStyles.estText}>
            <TextSmall>
              {' '}
              {t('exchange-account::glossary.est-account-opening-time-days', { days: estimatedAccountOpeningTime ?? '-' })}{' '}
            </TextSmall>
          </View>
          <View sx={sxStyles.buttonView}>
            <UniButton
              label={t('exchange-account::glossary.select-exchange')}
              onPress={() => {
                navigation.navigate(RootStackRoutes.ExchangeSubAccount, {
                  screen: ExchangeSubAccountStackRoutes.ConfirmExchange,
                  params: {
                    data,
                    shouldBeFetchFee: _.isEmpty(monthlyFee) || _.isEmpty(applicationFee),
                    onResult
                  }
                });
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  itemLayout: {
    flex: 1,
    borderRadius: '$radius2',
    mb: '$7',
    overflow: 'hidden',
    backgroundColor: '$backgroundPrimary'
  },
  itemBanner: {
    height: '48%'
  },
  itemViewSmall: {
    flexDirection: 'column',
    padding: '$5'
  },
  itemView: {
    flexDirection: 'column',
    padding: '$5'
  },
  itemContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    mb: '$3'
  },
  feeText: {
    color: '$textSecondary',
    mb: 2
  },
  monthlyFeeText: {
    color: '$textSecondary',
    mb: 2,
    textAlign: 'right'
  },
  estText: {
    mb: '$15',
    marginLeft: -4
  },
  buttonView: {
    position: 'absolute',
    bottom: '$5',
    left: '$5',
    right: '$5'
  },
  itemLeft: {
    alignContent: 'left',
    textAlign: 'left'
  },
  itemRight: {
    alignContent: 'right',
    textAlign: 'right'
  },
  iconWrapper: {
    position: 'absolute',
    top: '$0',
    left: '$0',
    opacity: 0
  }
};
